/**
 * Form controls for settings and onboarding: a switch, a labelled slider and
 * a single-choice group (pills, chips or cards). All are real form controls
 * underneath (button role="switch", input type="range", radio inputs), so
 * keyboards, switch access and screen readers work without extra code.
 */
import { useId, type CSSProperties, type ReactNode } from 'react';
import './ui.css';

// ───────────── Toggle ─────────────

export interface ToggleProps {
  label: string;
  description?: ReactNode;
  checked: boolean;
  onChange(checked: boolean): void;
  disabled?: boolean;
}

export function Toggle({ label, description, checked, onChange, disabled }: ToggleProps) {
  const id = useId();
  const descId = `${id}-desc`;
  return (
    <div className="ui-toggle">
      <label className="ui-toggle__text" htmlFor={id}>
        <span className="ui-toggle__label">{label}</span>
        {description && (
          <span id={descId} className="ui-toggle__desc">
            {description}
          </span>
        )}
      </label>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={description ? descId : undefined}
        className="ui-toggle__switch"
        disabled={disabled}
        onClick={() => onChange(!checked)}
      >
        <span className="ui-toggle__knob" aria-hidden="true" />
      </button>
    </div>
  );
}

// ───────────── Slider ─────────────

export interface SliderProps {
  label: string;
  /** 0–1 */
  value: number;
  onChange(value: number): void;
  /** Text for the current value (default "70%"). */
  valueText?: string;
  disabled?: boolean;
  icon?: ReactNode;
}

export function Slider({ label, value, onChange, valueText, disabled, icon }: SliderProps) {
  const id = useId();
  const percent = Math.round(Math.min(1, Math.max(0, value)) * 100);
  const text = valueText ?? `${percent}%`;
  return (
    <div className="ui-slider">
      <label className="ui-slider__label" htmlFor={id}>
        {icon}
        <span>{label}</span>
      </label>
      <output className="ui-slider__value" htmlFor={id} aria-hidden="true">
        {text}
      </output>
      <input
        id={id}
        className="ui-slider__input"
        type="range"
        min={0}
        max={100}
        step={5}
        value={percent}
        aria-valuetext={text}
        disabled={disabled}
        style={{ '--fill': `${percent}%` } as CSSProperties}
        onChange={(e) => onChange(Number(e.currentTarget.value) / 100)}
      />
    </div>
  );
}

// ───────────── Choice group ─────────────

export interface Choice<T extends string | number> {
  value: T;
  label: ReactNode;
  description?: ReactNode;
  /** Optional language tag for a label written in another language (e.g. "Afrikaans"). */
  lang?: string;
}

export interface ChoiceGroupProps<T extends string | number> {
  /** Group name (visible legend unless `hideLegend`). */
  legend: string;
  hideLegend?: boolean;
  choices: readonly Choice<T>[];
  value: T | null;
  onChange(value: T): void;
  /** pills: segmented control; chips: wrapping filter chips; cards: big stacked options; grid: big square buttons. */
  variant?: 'pills' | 'chips' | 'cards' | 'grid';
  className?: string;
}

export function ChoiceGroup<T extends string | number>({
  legend,
  hideLegend = false,
  choices,
  value,
  onChange,
  variant = 'pills',
  className,
}: ChoiceGroupProps<T>) {
  const name = useId();
  return (
    <fieldset className={['ui-choices', `ui-choices--${variant}`, className].filter(Boolean).join(' ')}>
      <legend className={hideLegend ? 'visually-hidden' : 'ui-choices__legend'}>{legend}</legend>
      <div className="ui-choices__options">
        {choices.map((choice) => (
          <label key={String(choice.value)} className="ui-choice" lang={choice.lang}>
            <input
              type="radio"
              className="ui-choice__input"
              name={name}
              value={String(choice.value)}
              checked={value === choice.value}
              onChange={() => onChange(choice.value)}
            />
            <span className="ui-choice__label">{choice.label}</span>
            {choice.description && <span className="ui-choice__desc">{choice.description}</span>}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
